import { useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Link, Route, Routes, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Bell, Search, ShoppingBag, Upload, X, Plus, Package, LayoutDashboard, LogOut, Archive, Check, ChevronRight, Download, Pencil, User } from "lucide-react";
import axios from "axios";
import "./App.css";
import { Privacy, Terms, Shipping, Returns, Contact } from "./Legal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;
const LOGO = "/assets/midnyte/logo.jpeg";
const money = n => `₹${Number(n).toLocaleString("en-IN")}`;
// Customer-facing labels for the two collection buckets. DB values remain unchanged.
const COLLECTION_LABEL = { "IT BOY": "For him", "IT GIRL": "For her" };
const labelFor = c => COLLECTION_LABEL[c] || c;

// Turn any axios error into a safe, human-readable string. Backend errors can be:
//   - HTTPException → { detail: "message" }
//   - Pydantic 422  → { detail: [{ type, loc, msg, input, url }, ...] }
//   - Network / opaque → no `response.data`.
// Rendering the raw payload as a React child crashes the tree, so ALWAYS pass axios
// errors through this before storing them in component state.
function extractError(err, fallback = "Something went wrong. Please try again.") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    const parts = d.map(item => {
      if (!item || typeof item !== "object") return String(item || "");
      const field = Array.isArray(item.loc) ? item.loc.filter(x => x !== "body").join(".") : "";
      const msg = typeof item.msg === "string" ? item.msg : "Invalid value";
      return field ? `${field}: ${msg}` : msg;
    }).filter(Boolean);
    if (parts.length) return parts.join("; ");
  }
  if (d && typeof d === "object" && typeof d.msg === "string") return d.msg;
  if (typeof err?.message === "string" && !/request failed/i.test(err.message)) return err.message;
  return fallback;
}

const BrandMark = ({ size = 44 }) => (
  <span className="brand-mark" style={{ "--m": size + "px" }} data-testid="brand-mark">
    <img src={LOGO} alt="MIDNYTE" />
  </span>
);

function useProducts(params = "") {
  // data: array | null = loading, "error" = temporary failure, [] = genuinely empty success.
  const [data, setData] = useState([]); const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true; setLoading(true); setError(false);
    axios.get(`${API}/products${params}`)
      .then(r => { if (active) { setData(Array.isArray(r.data) ? r.data : []); setError(false); } })
      .catch(() => { if (active) { setData([]); setError(true); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [params]);
  return { data, loading, error };
}

function useCountdown(targetIso) {
  const [ms, setMs] = useState(() => targetIso ? new Date(targetIso).getTime() - Date.now() : 0);
  useEffect(() => { if (!targetIso) return; const t = setInterval(() => setMs(new Date(targetIso).getTime() - Date.now()), 1000); return () => clearInterval(t); }, [targetIso]);
  if (!targetIso || ms <= 0) return null;
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
  return { d, h, m, s };
}

function useEscape(active, onClose) {
  useEffect(() => {
    if (!active) return;
    const h = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [active, onClose]);
}

function useScrollLock(active) {
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [active]);
}

function Countdown({ targetIso, label = "DROP LANDS IN" }) {
  const c = useCountdown(targetIso); if (!c) return null;
  return <div className="countdown" data-testid="drop-countdown">
    <p className="eyebrow">{label}</p>
    <div className="countdown-grid">
      {[["D", c.d], ["H", c.h], ["M", c.m], ["S", c.s]].map(([k, v]) =>
        <div key={k}><b>{String(v).padStart(2, "0")}</b><span>{k}</span></div>)}
    </div>
  </div>;
}

function Header({ cartCount, onCart, me, onSignOut }) {
  const [open, setOpen] = useState(false);
  return <header className="topbar" data-testid="store-header">
    <button className="menu-btn" onClick={() => setOpen(!open)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} data-testid="menu-toggle"><span /><span /><span /></button>
    <Link to="/" className="logo" data-testid="brand-logo" onClick={() => setOpen(false)}><BrandMark size={54} /></Link>
    <nav className={`desktop-nav ${open ? "open" : ""}`} onClick={() => setOpen(false)} aria-label="Primary">
      <Link to="/shop" data-testid="nav-shop-link">SHOP</Link>
      <Link to="/shop?sort=newest" data-testid="nav-new-link">NEW DROP</Link>
      <Link to="/shop?collection=IT%20BOY" data-testid="nav-for-him-link">FOR HIM</Link>
      <Link to="/shop?collection=IT%20GIRL" data-testid="nav-for-her-link">FOR HER</Link>
    </nav>
    <div className="header-actions">
      <Link to="/shop" aria-label="Search products" data-testid="header-search-link"><Search size={19} aria-hidden="true" /></Link>
      {me
        ? <Link to="/account" aria-label="Your account" data-testid="header-account-link" className="account-pill"><User size={18} aria-hidden="true" /><span>Account</span></Link>
        : <Link to="/login" aria-label="Sign in" data-testid="header-login-link" className="account-pill"><User size={18} aria-hidden="true" /><span>Sign in</span></Link>}
      {me && <button onClick={onSignOut} className="signout-btn" data-testid="header-signout" aria-label="Sign out"><LogOut size={16} aria-hidden="true" /></button>}
      <button onClick={onCart} aria-label={`Open bag, ${cartCount} items`} className="bag-icon" data-testid="header-cart-button"><ShoppingBag size={19} aria-hidden="true" /><b>{cartCount}</b></button>
    </div>
  </header>;
}

function ProductCard({ p, onAdd }) {
  return <article className="product-card" data-testid={`product-card-${p.id}`}>
    <Link to={`/product/${p.slug}`} className="product-image">
      <img src={p.images?.[0] || LOGO} alt={p.name} loading="lazy" />
      <div className="badges">{(p.badges || []).slice(0, 2).map(x => <span key={x} className={x === "SOLD OUT" ? "badge-sold" : "badge"}>{x}</span>)}</div>
      {/* wishlist feature removed — the heart button did nothing */}
    </Link>
    <div className="product-meta">
      <div>
        <Link to={`/product/${p.slug}`} className="product-name" data-testid={`product-name-${p.id}`}>{p.name}</Link>
        <p className="product-sub">{labelFor(p.collection || p.category)} · {p.category}</p>
      </div>
      <div className="price-block">
        <strong>{money(p.price)}</strong>
        {p.mrp > p.price && <del>{money(p.mrp)}</del>}
      </div>
    </div>
    <button className="quick-add" onClick={() => onAdd(p)} disabled={p.stock <= 0} data-testid={`add-to-bag-${p.id}`}>
      {p.stock <= 0 ? "SOLD OUT" : "ADD TO BAG"}<Plus size={16} aria-hidden="true" />
    </button>
  </article>;
}

function Home({ onAdd }) {
  const { data: products } = useProducts("");
  const [settings, setSettings] = useState({ brand_name: "MIDNYTE", hero_headline: "WEAR YOUR MOOD.", hero_cta: "SHOP THE DROP", instagram: "", hero_drop_name: "DROP 001 / AFTER DARK", hero_launch_at: null });
  useEffect(() => { axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => { }); }, []);
  const limited = products.filter(p => p.limited_drop || (p.badges || []).includes("LIMITED")).slice(0, 4);
  const trending = products.filter(p => p.trending).slice(0, 4);
  const heroPic = products.find(p => (p.badges || []).includes("BESTSELLER"))?.images?.[0] || products[0]?.images?.[0] || LOGO;
  const genderTiles = [
    { key: "IT BOY", label: "For him", tag: "HIM", vibe: "Chrome. Chains. Chaos." },
    { key: "IT GIRL", label: "For her", tag: "HER", vibe: "Y2K. Sugar. Sparkle." },
  ];
  return <main>
    <section className="hero" data-testid="hero-section">
      <img src={heroPic} alt="MIDNYTE editorial piece" />
      <div className="hero-copy">
        <p className="eyebrow">{settings.hero_drop_name || "DROP 001 / AFTER DARK"}</p>
        <h1 data-testid="hero-headline">{settings.hero_headline}</h1>
        <p className="hero-tagline">FINISH THE FIT.</p>
        <Countdown targetIso={settings.hero_launch_at} />
        <Link className="button neon" to="/shop" data-testid="hero-shop-button">{settings.hero_cta}<ArrowRight size={17} aria-hidden="true" /></Link>
      </div>
    </section>
    <section className="band intro-band">
      <p className="eyebrow">NO RULES, JUST ENERGY</p>
      <h2>THE DETAILS MAKE<br /><i>THE LOOK.</i></h2>
      <p className="intro-copy">Street-ready jewellery for main characters, late nights, and every fit in between.</p>
    </section>
    <section className="section" data-testid="new-drop-section">
      <div className="section-heading">
        <div><p className="eyebrow">JUST LANDED</p><h2>NEW DROP</h2></div>
        <Link to="/shop" data-testid="new-drop-view-all">VIEW ALL <ArrowRight size={17} aria-hidden="true" /></Link>
      </div>
      <div className="product-grid">{products.slice(0, 4).map(p => <ProductCard key={p.id} p={p} onAdd={onAdd} />)}</div>
    </section>
    <section className="vibe-section" data-testid="collections-section">
      <div className="section-heading"><div><p className="eyebrow">PICK YOUR SIDE</p><h2>SHOP BY MOOD</h2></div></div>
      <div className="vibe-grid">{genderTiles.map(c => {
        const pic = products.find(p => p.collection === c.key)?.images?.[0] || LOGO;
        return <Link to={`/shop?collection=${encodeURIComponent(c.key)}`} key={c.key} className="vibe-tile" data-testid={`collection-${c.label.toLowerCase().replace(/\s+/g, "-")}-link`}>
          <img src={pic} alt={c.label} />
          <div className="vibe-overlay"><small>{c.tag}</small><span className="vibe-title">{c.label}</span><em>{c.vibe}</em><ChevronRight size={18} aria-hidden="true" /></div>
        </Link>;
      })}</div>
    </section>
    {trending.length > 0 && <section className="section">
      <div className="section-heading"><div><p className="eyebrow">RUNNING HOT</p><h2>TRENDING</h2></div></div>
      <div className="product-grid">{trending.map(p => <ProductCard key={p.id} p={p} onAdd={onAdd} />)}</div>
    </section>}
    <section className="limited-band">
      <div>
        <p className="eyebrow">REAL STOCK. REAL DROP.</p>
        <h2>LIMITED<br /><i>ENERGY.</i></h2>
        <p>Small batches. When it&apos;s gone, it&apos;s gone.</p>
        <Link className="button dark" to="/shop" data-testid="limited-shop-button">SHOP LIMITED <ArrowRight size={17} aria-hidden="true" /></Link>
      </div>
      <img src={limited[0]?.images?.[0] || heroPic} alt="Limited piece from the current drop" />
    </section>
  </main>;
}

function Shop({ onAdd }) {
  const loc = useLocation();
  const q = useMemo(() => new URLSearchParams(loc.search), [loc.search]);
  const [search, setSearch] = useState(q.get("search") || "");
  const [category, setCategory] = useState(q.get("category") || "");
  const [collection, setCollection] = useState(q.get("collection") || "");
  const [sort, setSort] = useState(q.get("sort") || "newest");
  useEffect(() => { setCollection(q.get("collection") || ""); setCategory(q.get("category") || ""); setSearch(q.get("search") || ""); }, [q]);
  const { data, loading, error } = useProducts(`?search=${encodeURIComponent(search)}&category=${encodeURIComponent(category)}&collection=${encodeURIComponent(collection)}&sort=${sort}`);
  const headline = collection ? labelFor(collection).toUpperCase() : (category || "SHOP ALL");
  return <main className="shop-page">
    <div className="shop-head">
      <div><p className="eyebrow">THE FULL ROTATION</p><h1>{headline}</h1></div>
      <span data-testid="shop-result-count">{data.length} PIECES</span>
    </div>
    <div className="shop-controls">
      <label className="searchbox">
        <Search size={18} aria-hidden="true" />
        <input aria-label="Search products" data-testid="product-search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search chains, rings, pant chains..." />
      </label>
      <select data-testid="collection-filter" aria-label="Filter by collection" value={collection} onChange={e => setCollection(e.target.value)}>
        <option value="">ALL MOODS</option>
        <option value="IT BOY">For him</option>
        <option value="IT GIRL">For her</option>
      </select>
      <select data-testid="category-filter" aria-label="Filter by category" value={category} onChange={e => setCategory(e.target.value)}>
        <option value="">ALL PIECES</option><option>Chains</option><option>Rings</option><option>Bracelets</option><option>Pant Chains</option><option>Necklaces</option>
      </select>
      <select data-testid="sort-select" aria-label="Sort products" value={sort} onChange={e => setSort(e.target.value)}>
        <option value="newest">NEWEST</option><option value="price-low">PRICE: LOW</option><option value="price-high">PRICE: HIGH</option>
      </select>
    </div>
    {loading
      ? <div className="empty-state" role="status" data-testid="products-loading">LOADING THE DROP...</div>
      : error
        ? <div className="empty-state" role="status" data-testid="products-error">COULDN’T LOAD THE DROP. CHECK YOUR CONNECTION AND TRY AGAIN.</div>
        : data.length
          ? <div className="product-grid">{data.map(p => <ProductCard key={p.id} p={p} onAdd={onAdd} />)}</div>
          : <div className="empty-state" role="status" data-testid="empty-products">NOTHING MATCHED. TRY ANOTHER MOOD.</div>}
  </main>;
}

function NotifyModal({ product, onClose }) {
  const [email, setEmail] = useState(""); const [msg, setMsg] = useState(""); const [sending, setSending] = useState(false);
  useEscape(true, onClose); useScrollLock(true);
  const submit = e => {
    e.preventDefault(); setSending(true);
    axios.post(`${API}/notify`, { email, product_id: product.id })
      .then(r => setMsg(r.data.message))
      .catch(() => setMsg("Something glitched. Try again."))
      .finally(() => setSending(false));
  };
  return <div className="modal-scrim" onClick={onClose}>
    <div className="modal-card" onClick={e => e.stopPropagation()} data-testid="notify-modal" role="dialog" aria-modal="true" aria-labelledby="notify-title">
      <button className="modal-close" onClick={onClose} aria-label="Close notify dialog"><X size={18} aria-hidden="true" /></button>
      <p className="eyebrow">BACK-IN-STOCK ALERT</p>
      <h3 id="notify-title">{product.name}</h3>
      <p className="notify-blurb">Drop your email — we&apos;ll ping you the second it&apos;s restocked.</p>
      {msg
        ? <div className="notify-success" data-testid="notify-success"><Check size={18} aria-hidden="true" /> {msg}</div>
        : <form onSubmit={submit}>
          <label className="visually-hidden" htmlFor="notify-email">Email address</label>
          <input id="notify-email" type="email" required placeholder="you@midnyte.studio" value={email} onChange={e => setEmail(e.target.value)} data-testid="notify-email" />
          <button className="button neon full" disabled={sending} data-testid="notify-submit">{sending ? "SENDING..." : "NOTIFY ME"}<Bell size={17} aria-hidden="true" /></button>
        </form>}
    </div>
  </div>;
}

function ProductPage({ onAdd }) {
  const { slug } = useParams();
  // p: null = loading, false = not found (404), "error" = temporary failure (5xx/network).
  const [p, setP] = useState(null); const [notify, setNotify] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  useEffect(() => {
    setActiveImage(0);
    axios.get(`${API}/products/${slug}`)
      .then(r => setP(r.data))
      .catch(e => {
        const status = e && e.response && e.response.status;
        setP(status === 404 ? false : "error");
      });
  }, [slug]);
  if (p === null) return <div className="empty-state" role="status">LOADING PIECE...</div>;
  if (p === "error") return <div className="empty-state" role="status" data-testid="product-detail-error">COULDN’T LOAD THIS PIECE. CHECK YOUR CONNECTION AND TRY AGAIN.</div>;
  if (!p) return <div className="empty-state" role="status">PIECE NOT FOUND</div>;
  const sold = p.stock <= 0;
  const hasDiscount = p.mrp > p.price;
  const images = (p.images && p.images.length) ? p.images : [LOGO];
  const primaryIdx = Math.min(activeImage, images.length - 1);
  return <main className="detail-page">
    <div className="gallery">
      <img src={images[primaryIdx]} alt={`${p.name} — view ${primaryIdx + 1}`} data-testid="product-detail-image" />
      <div className="gallery-thumbs" role="tablist" aria-label="Product views">{images.map((x, i) =>
        <button key={x + i} type="button" role="tab" aria-selected={i === primaryIdx}
          className={i === primaryIdx ? "gallery-thumb-active" : ""}
          onClick={() => setActiveImage(i)} data-testid={`gallery-thumb-${i}`}>
          <img src={x} alt={`${p.name} view ${i + 1}`} />
        </button>
      )}</div>
    </div>
    <div className="detail-copy">
      <p className="eyebrow">{labelFor(p.collection || p.category)} · {p.sku}</p>
      <h1 data-testid="product-detail-name">{p.name}</h1>
      <div className="detail-price">
        <strong>{money(p.price)}</strong>
        {hasDiscount && <><del aria-label={`Manufacturer's declared MRP ${money(p.mrp)}`}>{money(p.mrp)}</del><span>{Math.round((1 - p.price / p.mrp) * 100)}% OFF</span></>}
      </div>
      <p className="price-note">Price inclusive of all taxes.{hasDiscount && " Struck-through amount is the manufacturer's declared MRP."}</p>
      <p className={`availability ${sold ? "sold" : ""}`} data-testid="product-availability">{p.availability}</p>
      <p className="description">{p.description}</p>
      <div className="specs"><span>MATERIAL <b>{p.material}</b></span><span>COLOUR <b>{p.colour}</b></span><span>SIZE <b>{p.size}</b></span></div>
      {sold
        ? <button className="button neon full" onClick={() => setNotify(true)} data-testid="notify-me-button">NOTIFY ME WHEN BACK<Bell size={18} aria-hidden="true" /></button>
        : <button className="button neon full" onClick={() => onAdd(p)} data-testid="product-add-to-bag-button">ADD TO BAG<ShoppingBag size={18} aria-hidden="true" /></button>}
      {/* ADD TO WISHLIST removed — non-functional */}
      <div className="info-lines">
        <div>SHIPPING <span><Link to="/shipping">Bangalore only · see shipping policy</Link></span></div>
        <div>RETURNS <span><Link to="/returns">2-day return window</Link></span></div>
      </div>
      <ProductCompliance product={p} />
    </div>
    {notify && <NotifyModal product={p} onClose={() => setNotify(false)} />}
  </main>;
}

function ProductCompliance({ product }) {
  const missing = () => <span className="legal-missing" data-testid="compliance-missing">&lt;REQUIRED FROM SELLER&gt;</span>;
  return <section className="compliance-box" data-testid="product-compliance" aria-label="Product compliance information">
    <h3>PRODUCT INFORMATION</h3>
    <dl>
      <dt>Generic name</dt><dd>{product.category || missing()}</dd>
      <dt>Net quantity</dt><dd>{product.net_quantity || "1"}</dd>
      <dt>MRP (inclusive of all taxes)</dt><dd>{money(product.mrp || product.price)}</dd>
      <dt>Country of origin</dt><dd>{product.country_of_origin || "India"}</dd>
      <dt>Manufacturer / Packer / Importer</dt><dd>{product.manufacturer || missing()}</dd>
      <dt>Year of manufacture</dt><dd>{product.mfg_year || "2026"}</dd>
      <dt>Consumer care</dt><dd>Cynalp Enterprises · 9180242519 · <a href="mailto:customercare@midnyte.in">customercare@midnyte.in</a> · <Link to="/contact">Grievance officer</Link></dd>
    </dl>
  </section>;
}

function Cart({ cart, onClose, onUpdate, onCheckout }) {
  useEscape(true, onClose); useScrollLock(true);
  const subtotal = cart.reduce((a, x) => a + x.p.price * x.quantity, 0);
  return <aside className="cart-drawer" data-testid="cart-drawer" role="dialog" aria-modal="true" aria-labelledby="cart-title">
    <div className="drawer-head">
      <h2 id="cart-title">YOUR BAG <small>({cart.length})</small></h2>
      <button onClick={onClose} aria-label="Close bag" data-testid="close-cart-button"><X aria-hidden="true" /></button>
    </div>
    {cart.length === 0
      ? <div className="empty-state">YOUR BAG IS EMPTY.<br /><Link to="/shop" onClick={onClose}>FIND YOUR PIECES →</Link></div>
      : <>
        <div className="cart-items">{cart.map(({ p, quantity }) =>
          <div className="cart-item" key={p.id}>
            <img src={p.images?.[0] || LOGO} alt={p.name} />
            <div>
              <strong>{p.name}</strong>
              <span>{money(p.price)}</span>
              <div className="qty">
                <button onClick={() => onUpdate(p.id, -1)} data-testid={`decrease-${p.id}`} aria-label={`Decrease quantity of ${p.name}`}>−</button>
                <b aria-live="polite">{quantity}</b>
                <button onClick={() => onUpdate(p.id, 1)} data-testid={`increase-${p.id}`} aria-label={`Increase quantity of ${p.name}`}>+</button>
              </div>
            </div>
            <button className="remove" onClick={() => onUpdate(p.id, -quantity)} data-testid={`remove-${p.id}`} aria-label={`Remove ${p.name} from bag`}>REMOVE</button>
          </div>)}
        </div>
        <div className="cart-total"><span>SUBTOTAL</span><strong>{money(subtotal)}</strong></div>
        <p className="cart-note"><b>Delivery is charged separately</b> based on delivery distance and is payable by you directly to the delivery partner.</p>
        <button className="button neon full" onClick={onCheckout} data-testid="checkout-button">CHECKOUT <ArrowRight size={17} aria-hidden="true" /></button>
      </>}
  </aside>;
}

// ── Auth pages ────────────────────────────────────────────────
function useAuth() {
  const [me, setMe] = useState(undefined);
  const refresh = () => axios.get(`${API}/auth/me`).then(r => setMe(r.data)).catch(e => {
    // 401 = genuinely logged out → me = null (customer sees login prompts).
    // Network/5xx = temporary failure → keep `me` as undefined so the UI doesn't
    // falsely redirect a logged-in customer to /login during a flaky request.
    const status = e && e.response && e.response.status;
    if (status === 401) setMe(null);
    // else: leave me alone — next successful call will set it.
  });
  useEffect(() => { refresh(); }, []);
  const signOut = () => axios.post(`${API}/auth/logout`).finally(() => setMe(null));
  return { me, refresh, signOut };
}

function Login({ onAuthed }) {
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const nav = useNavigate(); const loc = useLocation();
  const next = new URLSearchParams(loc.search).get("next") || "/account";
  const submit = e => {
    e.preventDefault(); setErr(""); setBusy(true);
    axios.post(`${API}/auth/login`, f)
      .then(() => { onAuthed(); nav(next, { replace: true }); })
      .catch(e => setErr(extractError(e, "Sign in failed")))
      .finally(() => setBusy(false));
  };
  return <main className="auth-page">
    <p className="eyebrow">WELCOME BACK</p><h1>SIGN IN</h1>
    <form onSubmit={submit} data-testid="login-form" noValidate>
      <label className="field"><span>Email</span><input required type="email" autoComplete="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="login-email" /></label>
      <label className="field"><span>Password</span><input required type="password" autoComplete="current-password" value={f.password} onChange={e => setF({ ...f, password: e.target.value })} data-testid="login-password" /></label>
      {err && <p className="form-error" role="alert" data-testid="login-error">{err}</p>}
      <button className="button neon full" disabled={busy} data-testid="login-submit">{busy ? "..." : "SIGN IN"}<ArrowRight size={17} aria-hidden="true" /></button>
    </form>
    <p className="auth-alt">New here? <Link to={`/register${loc.search}`} data-testid="link-to-register">Create an account →</Link></p>
  </main>;
}

function Register({ onAuthed }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const nav = useNavigate(); const loc = useLocation();
  const next = new URLSearchParams(loc.search).get("next") || "/account";
  const submit = e => {
    e.preventDefault(); setErr(""); setBusy(true);
    if (f.password.length < 8) { setErr("Password must be at least 8 characters"); setBusy(false); return; }
    axios.post(`${API}/auth/register`, f)
      .then(() => { onAuthed(); nav(next, { replace: true }); })
      .catch(e => setErr(extractError(e, "Could not create account")))
      .finally(() => setBusy(false));
  };
  return <main className="auth-page">
    <p className="eyebrow">JOIN THE ROTATION</p><h1>CREATE ACCOUNT</h1>
    <form onSubmit={submit} data-testid="register-form" noValidate>
      <label className="field"><span>Full name</span><input required autoComplete="name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="register-name" /></label>
      <label className="field"><span>Email</span><input required type="email" autoComplete="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="register-email" /></label>
      <label className="field"><span>Mobile number</span><input required inputMode="tel" autoComplete="tel" pattern="[0-9]{10}" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} data-testid="register-phone" /></label>
      <label className="field"><span>Password (min 8 chars)</span><input required type="password" autoComplete="new-password" minLength={8} value={f.password} onChange={e => setF({ ...f, password: e.target.value })} data-testid="register-password" /></label>
      {err && <p className="form-error" role="alert" data-testid="register-error">{err}</p>}
      <button className="button neon full" disabled={busy} data-testid="register-submit">{busy ? "..." : "CREATE ACCOUNT"}<ArrowRight size={17} aria-hidden="true" /></button>
    </form>
    <p className="auth-alt">Already have an account? <Link to={`/login${loc.search}`} data-testid="link-to-login">Sign in →</Link></p>
    <p className="terms-note">By creating an account you agree to our <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.</p>
  </main>;
}

function Account({ me, refresh }) {
  const [f, setF] = useState({ name: "", phone: "", address: "", city: "", state: "", pincode: "" });
  const [msg, setMsg] = useState(""); const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { if (me) setF(x => ({ ...x, name: me.name || "", phone: me.phone || "", address: me.address || "", city: me.city || "", state: me.state || "", pincode: me.pincode || "" })); }, [me]);
  const submit = e => {
    e.preventDefault(); setErr(""); setMsg(""); setBusy(true);
    axios.put(`${API}/auth/me`, f)
      .then(() => { setMsg("Profile saved."); refresh(); })
      .catch(e => setErr(extractError(e, "Could not save")))
      .finally(() => setBusy(false));
  };
  return <main className="auth-page">
    <p className="eyebrow">HELLO, {(me?.name || "").toUpperCase() || "MEMBER"}</p><h1>YOUR ACCOUNT</h1>
    <p className="account-email" data-testid="account-email">{me?.email}</p>
    <form onSubmit={submit} data-testid="account-form" noValidate>
      <label className="field"><span>Full name</span><input required autoComplete="name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="account-name" /></label>
      <label className="field"><span>Mobile number</span><input required inputMode="tel" autoComplete="tel" pattern="[0-9]{10}" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} data-testid="account-phone" /></label>
      <label className="field"><span>Address</span><input required autoComplete="street-address" value={f.address} onChange={e => setF({ ...f, address: e.target.value })} data-testid="account-address" /></label>
      <div className="form-row">
        <label className="field"><span>City</span><input required autoComplete="address-level2" value={f.city} onChange={e => setF({ ...f, city: e.target.value })} data-testid="account-city" /></label>
        <label className="field"><span>State</span><input required autoComplete="address-level1" value={f.state} onChange={e => setF({ ...f, state: e.target.value })} data-testid="account-state" /></label>
        <label className="field"><span>Pincode</span><input required inputMode="numeric" autoComplete="postal-code" pattern="[0-9]{6}" value={f.pincode} onChange={e => setF({ ...f, pincode: e.target.value })} data-testid="account-pincode" /></label>
      </div>
      {err && <p className="form-error" role="alert" data-testid="account-error">{err}</p>}
      {msg && <p className="form-success" role="status" data-testid="account-success">{msg}</p>}
      <button className="button neon full" disabled={busy} data-testid="account-save">{busy ? "SAVING..." : "SAVE PROFILE"}<Check size={17} aria-hidden="true" /></button>
    </form>
    <p className="auth-alt"><Link to="/orders" data-testid="link-my-orders">My orders →</Link></p>
    <p className="delivery-note">MIDNYTE currently delivers only within Bangalore (pincodes starting 560/561/562).</p>
  </main>;
}

function statusLabel(s) {
  return {
    PAYMENT_PENDING: "Payment Pending",
    PAYMENT_VERIFICATION_PENDING: "Payment Verification Pending",
    CONFIRMED: "Confirmed",
    PAYMENT_REJECTED: "Payment Rejected",
    PAID: "Paid",
    REJECTED: "Rejected",
    PACKED: "Packed", SHIPPED: "Shipped", DELIVERED: "Delivered", CANCELLED: "Cancelled",
    RETURN_REQUESTED: "Return Requested", RETURN_APPROVED: "Return Approved",
    RETURN_RECEIVED: "Return Received", REFUND_PROCESSED: "Refund Processed", RETURN_REJECTED: "Return Rejected",
  }[s] || s;
}

// Derive a canonical customer-facing state for the order card list.
function derivedState(o) {
  if (o.return_status && o.return_status !== "RETURN_REJECTED") return statusLabel(o.return_status);
  if (o.payment_status === "PAYMENT_PENDING") return "Payment Pending";
  if (o.payment_status === "PAYMENT_VERIFICATION_PENDING") return "Payment Verification Pending";
  if (o.payment_status === "REJECTED") return "Payment Rejected";
  if (o.fulfillment_status) return statusLabel(o.fulfillment_status);
  return "Confirmed";
}

const CUSTOMER_TIMELINE = ["ORDER_PLACED", "PAYMENT_VERIFIED", "CONFIRMED", "PACKED", "SHIPPED", "DELIVERED"];
function timelineState(o, step) {
  // Returns "done", "current", or "todo".
  const p = o.payment_status;
  const f = (o.fulfillment_status || "").toUpperCase();
  const fIndex = FULF_ORDER.indexOf(f);
  switch (step) {
    case "ORDER_PLACED": return "done";
    case "PAYMENT_VERIFIED":
      if (p === "PAID") return "done";
      if (p === "PAYMENT_VERIFICATION_PENDING") return "current";
      if (p === "REJECTED") return "rejected";
      return "current";
    case "CONFIRMED":
      if (p !== "PAID") return "todo";
      if (fIndex >= 0) return "done";
      return "current";
    case "PACKED": return fIndex >= 1 ? "done" : (fIndex === 0 ? "current" : "todo");
    case "SHIPPED": return fIndex >= 2 ? "done" : (fIndex === 1 ? "current" : "todo");
    case "DELIVERED": return fIndex >= 3 ? "done" : (fIndex === 2 ? "current" : "todo");
    default: return "todo";
  }
}
const FULF_ORDER = ["CONFIRMED", "PACKED", "SHIPPED", "DELIVERED"];

function OrderTimeline({ order }) {
  if (order.payment_status === "REJECTED") {
    return <section className="order-timeline" data-testid="order-timeline" aria-label="Order timeline">
      <div className="tl-step done"><span /><b>Order Placed</b></div>
      <div className="tl-step rejected"><span /><b>Payment Rejected</b></div>
    </section>;
  }
  const paymentLabel = order.payment_status === "PAID" ? "Payment Verified"
    : order.payment_status === "PAYMENT_VERIFICATION_PENDING" ? "Payment Verification"
    : "Payment";
  const rows = [
    ["Order Placed", "ORDER_PLACED"], [paymentLabel, "PAYMENT_VERIFIED"],
    ["Confirmed", "CONFIRMED"], ["Packed", "PACKED"], ["Shipped", "SHIPPED"], ["Delivered", "DELIVERED"],
  ];
  return <section className="order-timeline" data-testid="order-timeline" aria-label="Order timeline">
    {rows.map(([label, key]) => {
      const st = timelineState(order, key);
      return <div className={`tl-step ${st}`} key={key}><span aria-hidden="true" /><b>{label}</b></div>;
    })}
  </section>;
}

function Orders() {
  const [rows, setRows] = useState(null);
  useEffect(() => { axios.get(`${API}/orders`).then(r => setRows(r.data)).catch(() => setRows([])); }, []);
  if (rows === null) return <main className="auth-page"><p className="eyebrow">LOADING...</p></main>;
  return <main className="orders-page">
    <p className="eyebrow">YOUR ROTATION</p><h1>MY ORDERS</h1>
    {rows.length === 0
      ? <div className="empty-state" data-testid="orders-empty">You haven&apos;t placed any orders yet. <Link to="/shop">Start shopping →</Link></div>
      : <div className="order-list" data-testid="orders-list">
        {rows.map(o => <Link to={`/orders/${o.id}`} className="order-card" key={o.id} data-testid={`order-row-${o.id}`}>
          <div>
            <strong>{o.id}</strong>
            <span>{new Date(o.created_at).toLocaleString()}</span>
          </div>
          <div className="order-card-right">
            <em className="order-badge">{derivedState(o)}</em>
            <b>{money(o.total)}</b>
          </div>
        </Link>)}
      </div>}
  </main>;
}

function ReturnRequestBox({ orderId, onDone }) {
  const [reason, setReason] = useState(""); const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = e => {
    e.preventDefault(); setErr("");
    if (reason.trim().length < 3) { setErr("Please share a short reason for the return."); return; }
    setBusy(true);
    axios.post(`${API}/orders/${orderId}/return`, { reason: reason.trim() })
      .then(() => onDone())
      .catch(e => setErr(extractError(e, "Could not submit return request")))
      .finally(() => setBusy(false));
  };
  return <section className="return-box" data-testid="return-box">
    <h2>Request a return</h2>
    <p className="pay-note-small">Returns are accepted within 2 days after delivery. Once approved, ship the piece to our return address and we&apos;ll refund within 2 days of receipt.</p>
    <form onSubmit={submit}>
      <label className="field"><span>Reason for return</span>
        <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} data-testid="return-reason" maxLength={500} />
      </label>
      {err && <p className="form-error" role="alert" data-testid="return-error">{err}</p>}
      <button className="button neon" disabled={busy} data-testid="return-submit">{busy ? "SUBMITTING..." : "REQUEST RETURN"}<ArrowRight size={15} aria-hidden="true" /></button>
    </form>
  </section>;
}

function OrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(undefined);
  const load = () => axios.get(`${API}/orders/${id}`).then(r => setO(r.data)).catch(() => setO(null));
  useEffect(() => { axios.get(`${API}/orders/${id}`).then(r => setO(r.data)).catch(() => setO(null)); }, [id]);
  if (o === undefined) return <main className="auth-page"><p className="eyebrow">LOADING...</p></main>;
  if (!o) return <main className="auth-page"><p className="eyebrow">Not found</p><Link to="/orders">← Back to my orders</Link></main>;
  const needsPayment = o.payment_status === "PAYMENT_PENDING" || o.payment_status === "REJECTED";
  const canReturn = (o.fulfillment_status || "").toUpperCase() === "DELIVERED" && !o.return_status;
  return <main className="order-detail-page" data-testid="order-detail">
    <p className="eyebrow">ORDER {o.id}</p><h1>{derivedState(o)}</h1>
    <p className="order-meta">Placed {new Date(o.created_at).toLocaleString()}</p>

    <div className="status-strip" data-testid="status-strip">
      <div><small>Payment</small><em data-testid="badge-payment">{statusLabel(o.payment_status)}</em></div>
      <div><small>Fulfilment</small><em data-testid="badge-fulfillment">{o.fulfillment_status ? statusLabel(o.fulfillment_status) : "—"}</em></div>
      <div><small>Return</small><em data-testid="badge-return">{o.return_status ? statusLabel(o.return_status) : "No return requested"}</em></div>
    </div>

    <OrderTimeline order={o} />

    <section className="order-summary">
      {o.items.map(it => <div key={it.product_id} className="order-line" data-testid={`line-${it.product_id}`}>
        <img src={it.image || LOGO} alt={it.name} />
        <div>
          <strong>{it.name}</strong>
          <span>SKU: {it.sku || "-"} · Qty {it.quantity} · {money(it.price)}</span>
        </div>
        <b>{money(it.price * it.quantity)}</b>
      </div>)}
      <div className="order-totals">
        <div><span>Subtotal</span><b>{money(o.subtotal)}</b></div>
        <div><span>Delivery</span><b>{o.delivery_charge > 0 ? money(o.delivery_charge) : "Pay separately to delivery partner"}</b></div>
        <div className="order-grand"><span>Total</span><b data-testid="order-total">{money(o.total)}</b></div>
      </div>
    </section>

    <section className="order-mini-grid">
      <div className="order-mini">
        <p className="eyebrow">DELIVER TO</p>
        <p>{o.customer.customer_name}<br />{o.customer.address}<br />{o.customer.city} {o.customer.pincode}, {o.customer.state}<br />{o.customer.phone}</p>
      </div>
      <div className="order-mini">
        <p className="eyebrow">PAYMENT</p>
        <p>Method: UPI<br />Status: {statusLabel(o.payment_status)}<br />{o.payment_utr && <>Reference: {o.payment_utr}<br /></>}Amount: {money(o.total)}</p>
      </div>
    </section>

    {needsPayment && <PaymentSubmit orderId={o.id} amount={o.total} onDone={load} />}
    {o.payment_status === "PAYMENT_VERIFICATION_PENDING" && <div className="pay-note" data-testid="await-verification">Payment submitted. Your order will be confirmed after payment verification by our team.</div>}
    {canReturn && <ReturnRequestBox orderId={o.id} onDone={load} />}
    {o.return_status === "REFUND_PROCESSED" && <div className="pay-success">Refund processed.</div>}

    <p className="auth-alt"><Link to="/orders">← All orders</Link></p>
  </main>;
}

function PaymentSubmit({ orderId, amount, onDone }) {
  const [utr, setUtr] = useState(""); const [paidAt, setPaidAt] = useState(""); const [file, setFile] = useState(null);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false); const [help, setHelp] = useState(false);
  const submit = e => {
    e.preventDefault(); setErr("");
    const cleanUtr = utr.trim();
    if (cleanUtr.length < 4) { setErr("Please enter your transaction reference number (at least 4 characters)."); return; }
    if (cleanUtr.length > 64) { setErr("Transaction reference is too long — maximum 64 characters."); return; }
    if (!paidAt) { setErr("Please enter the payment date and time."); return; }
    if (!file) { setErr("Please attach your payment screenshot."); return; }
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { setErr("Screenshot must be a JPEG, PNG or WEBP image."); return; }
    if (file.size > 5 * 1024 * 1024) { setErr("Screenshot must be under 5 MB."); return; }
    const fd = new FormData(); fd.append("utr", cleanUtr); fd.append("paid_at", paidAt); fd.append("screenshot", file);
    setBusy(true);
    axios.post(`${API}/orders/${orderId}/payment`, fd).then(() => onDone()).catch(e => setErr(extractError(e, "Could not submit payment"))).finally(() => setBusy(false));
  };
  return <section className="pay-block" data-testid="payment-block">
    <div className="pay-copy">
      <p className="eyebrow">PAY WITH UPI</p>
      <h2>Pay <span data-testid="pay-amount">{money(amount)}</span></h2>
      <p>Scan the QR from any UPI app (GPay, PhonePe, Paytm, BHIM), then enter the transaction reference number and upload the payment screenshot below.</p>
      <img className="qr-image" src="/assets/midnyte/upi-qr.jpeg" alt="MIDNYTE UPI QR code — payee Prashanth Kumar" data-testid="upi-qr-image" />
      <p className="pay-note-small">Payee: Prashanth Kumar · UPI ID <code>prashantkumarnarsha-1@okicic</code></p>
      <p className="pay-note-small"><b>Never share your UPI PIN, OTP, card number, CVV or bank password.</b> MIDNYTE will never ask for these.</p>
    </div>
    <form onSubmit={submit} className="pay-form" data-testid="payment-form" encType="multipart/form-data" noValidate>
      <label className="field"><span>Transaction reference number</span>
        <input required value={utr} onChange={e => setUtr(e.target.value)} data-testid="pay-utr" minLength={4} maxLength={64} placeholder="e.g. AXIS20260920123456" />
      </label>
      <p className="pay-note-small">Enter the transaction reference number shown after you complete your payment. <button type="button" className="helper-toggle" onClick={() => setHelp(v => !v)} data-testid="pay-help-toggle" aria-expanded={help}>Where do I find this?</button></p>
      {help && <ul className="pay-helper" data-testid="pay-helper">
        <li><b>GPay</b>: open the completed payment → payment details → copy the transaction / reference number.</li>
        <li><b>PhonePe</b>: open the payment → transaction details → copy the transaction / reference number.</li>
        <li><b>Other UPI apps</b>: open the completed payment → transaction details → copy the transaction / reference number.</li>
      </ul>}
      <label className="field"><span>Payment date &amp; time</span>
        <input required type="datetime-local" value={paidAt} onChange={e => setPaidAt(e.target.value)} data-testid="pay-time" />
      </label>
      <label className="field"><span>Payment screenshot</span>
        <input required type="file" accept="image/jpeg,image/png,image/webp" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="pay-file" />
      </label>
      <p className="pay-note-small">Upload a screenshot showing that your payment was completed (JPEG / PNG / WEBP, max 5 MB).</p>
      {err && <p className="form-error" role="alert" data-testid="pay-error">{err}</p>}
      <button className="button neon full" disabled={busy} data-testid="pay-submit">{busy ? "SUBMITTING..." : "SUBMIT PAYMENT"}<ArrowRight size={17} aria-hidden="true" /></button>
    </form>
  </section>;
}

function Checkout({ cart, onDone, onCartSync, me }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  // Server-authoritative sync: before placing the order we re-fetch each cart item from the
  // backend and surface any price/stock changes so the customer isn't paying a stale amount.
  // priceSync: null = checking, {changed: [...], removed: [...]} = result, "error" = temporary failure.
  const [priceSync, setPriceSync] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  // A per-attempt idempotency key: retries share the same key (dedupe on the server),
  // a fresh mount / fresh error clears it so a genuinely-new checkout gets a new key.
  const idemRef = useRef(null);
  const subtotal = cart.reduce((a, x) => a + x.p.price * x.quantity, 0);
  const missingProfile = !me || !me.name || !me.address || !me.city || !me.pincode || !me.phone;

  useEffect(() => {
    if (!cart.length) { setPriceSync({ changed: [], removed: [] }); return; }
    let active = true;
    axios.get(`${API}/products`).then(r => {
      if (!active) return;
      const byId = new Map((r.data || []).map(p => [p.id, p]));
      const changed = []; const removed = []; const synced = [];
      for (const line of cart) {
        const latest = byId.get(line.p.id);
        if (!latest || !latest.published || latest.status === "ARCHIVED") {
          removed.push({ name: line.p.name, reason: "no longer available" });
          continue;
        }
        if (latest.stock < line.quantity) {
          removed.push({ name: line.p.name, reason: latest.stock > 0 ? `only ${latest.stock} left in stock` : "sold out" });
          continue;
        }
        const priceChanged = Number(latest.price) !== Number(line.p.price);
        if (priceChanged) changed.push({ name: line.p.name, oldPrice: line.p.price, newPrice: latest.price });
        synced.push({ p: { ...line.p, price: latest.price, stock: latest.stock, mrp: latest.mrp, status: latest.status }, quantity: line.quantity });
      }
      // Push a reconciled cart back to the parent so the summary totals reflect server-authoritative prices.
      if ((changed.length || removed.length) && typeof onCartSync === "function") onCartSync(synced);
      setPriceSync({ changed, removed });
      setAcknowledged(!(changed.length || removed.length));
    }).catch(() => { if (active) setPriceSync("error"); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const placeOrder = () => {
    if (busy) return;
    setErr(""); setBusy(true);
    if (!idemRef.current) idemRef.current = `mdn-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    axios.post(`${API}/checkout`,
      { items: cart.map(x => ({ product_id: x.p.id, quantity: x.quantity })) },
      { headers: { "Idempotency-Key": idemRef.current } })
      .then(r => { onDone(); nav(`/orders/${r.data.id}`); })
      .catch(e => { idemRef.current = null; setErr(extractError(e, "Could not place order")); })
      .finally(() => setBusy(false));
  };
  if (me === undefined) return null;
  if (!me) return <Navigate to="/login?next=/checkout" replace />;
  if (cart.length === 0) return <main className="auth-page"><p className="eyebrow">EMPTY</p><h1>YOUR BAG IS EMPTY</h1><Link to="/shop" className="button neon">Find your pieces →</Link></main>;
  const syncing = priceSync === null;
  const syncError = priceSync === "error";
  const changed = (priceSync && priceSync !== "error") ? priceSync.changed : [];
  const removed = (priceSync && priceSync !== "error") ? priceSync.removed : [];
  const hasChanges = changed.length > 0 || removed.length > 0;
  const canOrder = !syncing && !syncError && (!hasChanges || acknowledged) && removed.length === 0;
  return <main className="checkout-page">
    <div>
      <p className="eyebrow">ONE LAST THING</p><h1>CHECKOUT</h1>
      <section className="checkout-address" data-testid="checkout-address-box">
        <div className="checkout-address-head">
          <h2>Delivery address</h2>
          <Link to={`/account?next=/checkout`} data-testid="edit-address-link">Edit</Link>
        </div>
        {missingProfile
          ? <p className="form-error" data-testid="missing-profile">Please complete your delivery address in your account before placing the order.</p>
          : <p data-testid="checkout-address-lines">{me.name}<br />{me.address}<br />{me.city} {me.pincode}, {me.state}<br />{me.phone}</p>}
      </section>
      {syncing && <p className="form-error" role="status" data-testid="cart-syncing" style={{ color: "var(--muted)" }}>Checking latest prices &amp; stock...</p>}
      {syncError && <p className="form-error" role="alert" data-testid="cart-sync-error">Couldn’t verify latest prices. Please refresh and try again.</p>}
      {hasChanges && <section className="cart-sync-notice" data-testid="cart-sync-notice" role="alert">
        <h2>Your bag changed since you added these pieces</h2>
        {changed.length > 0 && <ul data-testid="cart-sync-changed">{changed.map((c, i) =>
          <li key={i}><b>{c.name}</b>: price updated from {money(c.oldPrice)} to <b>{money(c.newPrice)}</b></li>)}</ul>}
        {removed.length > 0 && <ul data-testid="cart-sync-removed">{removed.map((r, i) =>
          <li key={i}><b>{r.name}</b>: {r.reason}. Remove this item from your bag to continue.</li>)}</ul>}
        {removed.length === 0 && !acknowledged &&
          <button type="button" className="button neon" data-testid="cart-sync-accept" onClick={() => setAcknowledged(true)}>REVIEW &amp; CONTINUE WITH UPDATED PRICES</button>}
      </section>}
      <section className="checkout-lines">
        <h2>Your pieces</h2>
        {cart.map(({ p, quantity }) => <div key={p.id} className="checkout-line" data-testid={`checkout-line-${p.id}`}>
          <img src={p.images?.[0] || LOGO} alt={p.name} />
          <div><strong>{p.name}</strong><span>Qty {quantity} · {money(p.price)}</span></div>
          <b>{money(p.price * quantity)}</b>
        </div>)}
      </section>
      <div className="checkout-totals">
        <div><span>Product subtotal</span><b>{money(subtotal)}</b></div>
        <div><span>Delivery</span><b>Pay separately to delivery partner</b></div>
        <div className="grand"><span>Amount payable to MIDNYTE</span><b data-testid="checkout-total">{money(subtotal)}</b></div>
      </div>
      <p className="delivery-note"><b>Delivery is charged separately</b> based on delivery distance and is payable by you to the delivery partner (not included in the UPI amount paid to MIDNYTE).</p>
      <p className="terms-note">Payment method: UPI only (COD is not available). By placing this order you agree to our <Link to="/terms">Terms</Link>, <Link to="/privacy">Privacy Policy</Link> and <Link to="/returns">Return &amp; Refund Policy</Link>.</p>
      <p className="delivery-note" data-testid="bangalore-note">MIDNYTE currently delivers only within Bangalore (pincodes 560xxx – 562xxx).</p>
      {err && <p className="form-error" role="alert" data-testid="checkout-error">{err}</p>}
      <button className="button neon full" disabled={busy || missingProfile || !canOrder} onClick={placeOrder} data-testid="place-order-button">{busy ? "PLACING ORDER..." : "PLACE ORDER & PAY"}<ArrowRight size={17} aria-hidden="true" /></button>
    </div>
  </main>;
}

// ── Admin (hidden route) ────────────────────────────────────────
function ProductImageUploader({ images, onChange }) {
  // Accepts a comma-separated image list (same contract as the existing ProductForm field).
  // Uploads selected files to the backend which stores them in Emergent Object Storage and
  // returns a stable URL — appended to the list. The textarea remains editable for power users.
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const list = images.split(",").map(s => s.trim()).filter(Boolean);
  const commit = next => onChange(next.join(", "));
  const upload = async (filesList) => {
    setErr(""); setBusy(true);
    const next = [...list];
    try {
      for (const file of filesList) {
        if (!file.type.startsWith("image/")) { setErr("Only image files are allowed."); continue; }
        if (file.size > 5 * 1024 * 1024) { setErr(`"${file.name}" is bigger than 5 MB — pick a smaller image.`); continue; }
        const form = new FormData(); form.append("file", file);
        const { data } = await axios.post(`${API}/admin/upload-image`, form);
        next.push(data.url);
      }
      commit(next);
    } catch (e) {
      setErr(extractError(e, "Could not upload image. Please try again."));
    } finally { setBusy(false); }
  };
  const removeAt = i => { const next = [...list]; next.splice(i, 1); commit(next); };
  return <div className="image-uploader span-2" data-testid="product-image-uploader">
    <span className="field-label">PRODUCT IMAGES</span>
    <div className="image-thumbs">
      {list.length === 0 && <p className="muted">No images yet — upload at least one so the product looks good on the storefront.</p>}
      {list.map((src, i) => <div className="image-thumb" key={`${src}-${i}`}>
        <img src={src.startsWith("http") || src.startsWith("/") ? src : src} alt={`Product preview ${i + 1}`} />
        <button type="button" className="thumb-remove" aria-label={`Remove image ${i + 1}`} onClick={() => removeAt(i)}>×</button>
      </div>)}
    </div>
    <div className="image-controls">
      <label className="upload-btn button ghost small">
        {busy ? "UPLOADING..." : "UPLOAD IMAGES"}
        <input type="file" accept="image/jpeg,image/png,image/webp" multiple
          disabled={busy} data-testid="product-image-input"
          onChange={e => { const fs = Array.from(e.target.files || []); if (fs.length) upload(fs); e.target.value = ""; }} />
      </label>
      <small className="muted">JPEG, PNG or WEBP. Up to 5 MB each.</small>
    </div>
    {err && <p className="form-error">{err}</p>}
    <details className="image-advanced">
      <summary>Prefer to paste URLs? (advanced)</summary>
      <input value={images} onChange={e => onChange(e.target.value)} placeholder="comma-separated URLs"
        data-testid="pf-images-advanced" />
    </details>
  </div>;
}

function ProductForm({ initial, submitLabel, onSubmit, onClose, mode }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  useEscape(true, onClose); useScrollLock(true);
  const setV = (k, v) => setF({ ...f, [k]: v });
  const submit = async e => {
    e.preventDefault(); setErr("");
    const price = Number(f.price), mrp = Number(f.mrp), stock = Number(f.stock);
    if (!f.name.trim()) return setErr("Product name is required.");
    if (mode === "add" && !f.sku.trim()) return setErr("SKU is required.");
    if (!(price > 0)) return setErr("Price must be greater than 0.");
    if (!(mrp > 0)) return setErr("MRP must be greater than 0.");
    if (mrp < price) return setErr("MRP must be equal to or higher than the selling price.");
    if (mode === "add" && !(stock >= 0)) return setErr("Stock cannot be negative.");
    if (f.published && !(f.manufacturer || "").trim()) return setErr("Manufacturer is required for published products.");
    setBusy(true);
    const payload = {
      name: f.name.trim(),
      category: f.category, collection: f.collection,
      price, mrp,
      description: f.description, material: f.material, colour: f.colour, size: f.size,
      tags: f.tags.split(",").map(t => t.trim()).filter(Boolean),
      images: f.images_csv.split(",").map(t => t.trim()).filter(Boolean),
      badges: f.badges_csv.split(",").map(t => t.trim().toUpperCase()).filter(Boolean),
      published: !!f.published, featured: !!f.featured, trending: !!f.trending,
      is_new: !!f.is_new, best_seller: !!f.best_seller, limited_drop: !!f.limited_drop,
      low_stock_threshold: f.low_stock_threshold === "" || f.low_stock_threshold == null ? 3 : Number(f.low_stock_threshold),
      manufacturer: (f.manufacturer || "").trim(),
      net_quantity: (f.net_quantity || "1").trim(),
      country_of_origin: (f.country_of_origin || "India").trim(),
      mfg_year: (f.mfg_year || "").trim(),
    };
    if (mode === "add") {
      payload.sku = f.sku.trim().toUpperCase();
      payload.stock = stock;
      payload.status = "ACTIVE";
    }
    try { await onSubmit(payload); }
    catch (e) { setErr(extractError(e, mode === "add" ? "Could not create product" : "Could not save changes")); }
    finally { setBusy(false); }
  };
  return <div className="modal-scrim" onClick={onClose}>
    <div className="modal-card add-card" onClick={e => e.stopPropagation()} data-testid={`${mode}-product-modal`} role="dialog" aria-modal="true" aria-labelledby={`${mode}-product-title`}>
      <button className="modal-close" onClick={onClose} aria-label="Close dialog"><X size={18} aria-hidden="true" /></button>
      <p className="eyebrow">{mode === "add" ? "NEW PIECE" : "EDIT PIECE"}</p>
      <h3 id={`${mode}-product-title`}>{mode === "add" ? "ADD PRODUCT" : "EDIT PRODUCT"}</h3>
      <form onSubmit={submit} className="add-form" data-testid={`${mode}-product-form`}>
        <label><span>PRODUCT NAME</span><input value={f.name} onChange={e => setV("name", e.target.value)} data-testid="pf-name" required /></label>
        <label><span>SKU</span><input value={f.sku} onChange={e => setV("sku", e.target.value)} placeholder="e.g. MDN-RG-M-09" data-testid="pf-sku" disabled={mode !== "add"} title={mode === "add" ? "" : "SKU cannot be changed after creation"} /></label>
        <label><span>COLLECTION</span><select value={f.collection} onChange={e => setV("collection", e.target.value)} data-testid="pf-collection"><option value="IT BOY">For him</option><option value="IT GIRL">For her</option><option value="NO LABELS">No labels</option></select></label>
        <label><span>CATEGORY</span><select value={f.category} onChange={e => setV("category", e.target.value)} data-testid="pf-category"><option>Rings</option><option>Chains</option><option>Bracelets</option><option>Pant Chains</option><option>Necklaces</option><option>Earrings</option></select></label>
        <label><span>SELLING PRICE (₹)</span><input type="number" min={1} value={f.price} onChange={e => setV("price", e.target.value)} data-testid="pf-price" /></label>
        <label><span>MRP (₹)</span><input type="number" min={1} value={f.mrp} onChange={e => setV("mrp", e.target.value)} data-testid="pf-mrp" /></label>
        {mode === "add" && <label><span>STOCK</span><input type="number" min={0} value={f.stock} onChange={e => setV("stock", e.target.value)} data-testid="pf-stock" /></label>}
        <label><span>LOW-STOCK ALERT AT</span><input type="number" min={0} value={f.low_stock_threshold} onChange={e => setV("low_stock_threshold", e.target.value)} data-testid="pf-low" /></label>
        <label><span>MATERIAL</span><input value={f.material} onChange={e => setV("material", e.target.value)} data-testid="pf-material" /></label>
        <label><span>COLOUR</span><input value={f.colour} onChange={e => setV("colour", e.target.value)} data-testid="pf-colour" /></label>
        <label className="span-2"><span>SIZE</span><input value={f.size} onChange={e => setV("size", e.target.value)} data-testid="pf-size" /></label>
        <ProductImageUploader images={f.images_csv} onChange={v => setV("images_csv", v)} />
        <label className="span-2"><span>TAGS (comma sep.)</span><input value={f.tags} onChange={e => setV("tags", e.target.value)} data-testid="pf-tags" /></label>
        <label className="span-2"><span>BADGES (comma sep.)</span><input value={f.badges_csv} onChange={e => setV("badges_csv", e.target.value)} placeholder="NEW, TRENDING" data-testid="pf-badges" /></label>
        <label className="span-2"><span>DESCRIPTION</span><textarea rows={3} value={f.description} onChange={e => setV("description", e.target.value)} data-testid="pf-description" /></label>
        <p className="span-2 compliance-hint" data-testid="compliance-hint">
          <b>LEGAL METROLOGY COMPLIANCE.</b> For published products the <b>Manufacturer / Packer / Importer</b>
          field is <b>mandatory</b>. Net quantity, country of origin and year of manufacture are optional —
          when left blank they default to "1", "India", and the current year on the public product page.
          Unpublished products accept any (or empty) compliance fields.
        </p>
        <label className="span-2"><span>MANUFACTURER / PACKER / IMPORTER *</span><input value={f.manufacturer || ""} onChange={e => setV("manufacturer", e.target.value)} placeholder="e.g. Cynalp Enterprises, Bangalore 560103" data-testid="pf-manufacturer" required={!!f.published} /></label>
        <label><span>NET QUANTITY</span><input value={f.net_quantity || ""} onChange={e => setV("net_quantity", e.target.value)} placeholder="1 unit" data-testid="pf-netqty" /></label>
        <label><span>COUNTRY OF ORIGIN</span><input value={f.country_of_origin || ""} onChange={e => setV("country_of_origin", e.target.value)} placeholder="India" data-testid="pf-country" /></label>
        <label className="span-2"><span>YEAR OF MANUFACTURE</span><input value={f.mfg_year || ""} onChange={e => setV("mfg_year", e.target.value)} placeholder="e.g. 2026" data-testid="pf-mfgyear" /></label>
        <div className="span-2 flag-row">
          <label className="checkbox"><input type="checkbox" checked={f.published} onChange={e => setV("published", e.target.checked)} data-testid="pf-published" /> Published</label>
          <label className="checkbox"><input type="checkbox" checked={f.featured} onChange={e => setV("featured", e.target.checked)} data-testid="pf-featured" /> Featured</label>
          <label className="checkbox"><input type="checkbox" checked={f.trending} onChange={e => setV("trending", e.target.checked)} data-testid="pf-trending" /> Trending</label>
          <label className="checkbox"><input type="checkbox" checked={f.is_new} onChange={e => setV("is_new", e.target.checked)} data-testid="pf-new" /> New</label>
          <label className="checkbox"><input type="checkbox" checked={f.best_seller} onChange={e => setV("best_seller", e.target.checked)} data-testid="pf-bestseller" /> Best Seller</label>
          <label className="checkbox"><input type="checkbox" checked={f.limited_drop} onChange={e => setV("limited_drop", e.target.checked)} data-testid="pf-limited" /> Limited Drop</label>
        </div>
        {err && <p className="form-error span-2" data-testid="pf-error">{err}</p>}
        <div className="span-2 add-actions">
          <button type="button" className="button ghost" onClick={onClose}>CANCEL</button>
          <button className="button neon" disabled={busy} data-testid="pf-submit">{busy ? "SAVING..." : submitLabel}<ArrowRight size={15} aria-hidden="true" /></button>
        </div>
      </form>
    </div>
  </div>;
}

function AddProduct({ onCreated, onClose }) {
  const empty = { name: "", sku: "", category: "Rings", collection: "IT BOY", price: 299, mrp: 299, stock: 10, description: "", material: "316L stainless steel", colour: "Silver", size: "Adjustable", images_csv: "", badges_csv: "NEW", published: true, featured: false, trending: false, is_new: true, best_seller: false, limited_drop: false, low_stock_threshold: 3, tags: "", manufacturer: "", net_quantity: "1", country_of_origin: "India", mfg_year: String(new Date().getFullYear()) };
  return <ProductForm initial={empty} mode="add" submitLabel="CREATE PIECE" onClose={onClose}
    onSubmit={async payload => { await axios.post(`${API}/admin/products`, payload); onCreated(); }} />;
}

function EditProduct({ product, onSaved, onClose }) {
  const initial = {
    name: product.name || "", sku: product.sku || "",
    category: product.category || "Rings", collection: product.collection || "IT BOY",
    price: product.price ?? 0, mrp: product.mrp ?? 0,
    description: product.description || "", material: product.material || "",
    colour: product.colour || "", size: product.size || "",
    images_csv: (product.images || []).join(", "),
    badges_csv: (product.badges || []).join(", "),
    tags: (product.tags || []).join(", "),
    low_stock_threshold: product.low_stock_threshold ?? 3,
    manufacturer: product.manufacturer || "",
    net_quantity: product.net_quantity || "1",
    country_of_origin: product.country_of_origin || "India",
    mfg_year: product.mfg_year || "",
    published: !!product.published, featured: !!product.featured, trending: !!product.trending,
    is_new: !!product.is_new, best_seller: !!product.best_seller, limited_drop: !!product.limited_drop,
  };
  return <ProductForm initial={initial} mode="edit" submitLabel="SAVE CHANGES" onClose={onClose}
    onSubmit={async payload => { await axios.patch(`${API}/admin/products/${product.id}`, payload); onSaved(); }} />;
}

function StockCell({ product, onSaved }) {
  const [editing, setEditing] = useState(false); const [val, setVal] = useState(product.stock); const [msg, setMsg] = useState("");
  useEffect(() => setVal(product.stock), [product.stock]);
  const save = async () => {
    const n = Math.max(0, parseInt(val, 10) || 0);
    try {
      const r = await axios.patch(`${API}/admin/products/${product.id}/stock`, { stock: n });
      onSaved(); setEditing(false);
      if (r.data.status === "ACTIVE" && r.data.waitlist_count > 0) setMsg(`Restocked · ${r.data.waitlist_count} on waitlist`);
      setTimeout(() => setMsg(""), 4000);
    } catch { setMsg("Save failed"); }
  };
  if (editing) return <span className="stock-edit">
    <input type="number" min={0} value={val} onChange={e => setVal(e.target.value)} onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") { setEditing(false); setVal(product.stock); } }} autoFocus data-testid={`stock-input-${product.id}`} aria-label={`Stock for ${product.name}`} />
    <button onClick={save} data-testid={`stock-save-${product.id}`} aria-label="Save stock"><Check size={13} aria-hidden="true" /></button>
    <button onClick={() => { setEditing(false); setVal(product.stock); }} data-testid={`stock-cancel-${product.id}`} aria-label="Cancel"><X size={13} aria-hidden="true" /></button>
  </span>;
  return <span className={`stock-cell ${product.stock <= 3 ? "low-stock" : ""}`}>
    <button className="stock-value" onClick={() => setEditing(true)} data-testid={`stock-edit-${product.id}`} aria-label={`Edit stock for ${product.name}`}>{product.stock}<Pencil size={11} aria-hidden="true" /></button>
    {product.waitlist_count > 0 && <em className="waitlist-pill" title={`${product.waitlist_count} customers waiting`} data-testid={`waitlist-count-${product.id}`}><Bell size={10} aria-hidden="true" />{product.waitlist_count}</em>}
    {msg && <em className="stock-flash">{msg}</em>}
  </span>;
}

function BulkUpload({ onDone }) {
  const [file, setFile] = useState(null); const [result, setResult] = useState(null); const [busy, setBusy] = useState(false);
  const submit = async e => {
    e.preventDefault(); if (!file) return; setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const r = await axios.post(`${API}/admin/products/bulk-csv`, fd); setResult(r.data); onDone?.(); }
    catch (err) { setResult({ error: extractError(err, "Upload failed") }); }
    finally { setBusy(false); }
  };
  const template = () => {
    axios.get(`${API}/admin/products/bulk-template`, { responseType: "blob" }).then(r => {
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "midnyte-products-template.csv"; a.click(); URL.revokeObjectURL(url);
    });
  };
  return <div className="bulk-panel" data-testid="bulk-upload-panel">
    <div className="bulk-head"><h3>BULK UPLOAD</h3><button className="button ghost" onClick={template} data-testid="download-template-button"><Download size={15} aria-hidden="true" /> TEMPLATE</button></div>
    <form onSubmit={submit} className="bulk-form"><label className="file-drop" data-testid="bulk-file-drop"><Upload size={18} aria-hidden="true" /><span>{file ? file.name : "Drop your products.csv here"}</span><input type="file" accept=".csv" onChange={e => setFile(e.target.files?.[0])} data-testid="bulk-file-input" /></label><button className="button neon" disabled={!file || busy} data-testid="bulk-submit-button">{busy ? "UPLOADING..." : "UPLOAD"}<ArrowRight size={15} aria-hidden="true" /></button></form>
    {result && <div className="bulk-result" data-testid="bulk-result">{result.error ? <em className="form-error">{result.error}</em> : <><b>{result.inserted} inserted</b> · <b>{result.updated} updated</b>{result.errors?.length > 0 && <details><summary>{result.errors.length} row errors</summary><ul>{result.errors.slice(0, 10).map((e, i) => <li key={i}>Row {e.row} ({e.sku}): {e.error}</li>)}</ul></details>}</>}</div>}
  </div>;
}

function DropSettings({ settings, onSaved }) {
  const [form, setForm] = useState(settings);
  useEffect(() => setForm(settings), [settings]);
  const save = e => { e.preventDefault(); axios.put(`${API}/admin/settings`, form).then(() => onSaved(form)); };
  const localVal = form.hero_launch_at ? new Date(form.hero_launch_at).toISOString().slice(0, 16) : "";
  return <form className="drop-settings" onSubmit={save} data-testid="drop-settings-form">
    <div><label htmlFor="s-hero">Hero headline</label><input id="s-hero" value={form.hero_headline || ""} onChange={e => setForm({ ...form, hero_headline: e.target.value })} data-testid="settings-hero-headline" /></div>
    <div><label htmlFor="s-drop">Drop name</label><input id="s-drop" value={form.hero_drop_name || ""} onChange={e => setForm({ ...form, hero_drop_name: e.target.value })} data-testid="settings-drop-name" /></div>
    <div><label htmlFor="s-launch">Drop launches at</label><input id="s-launch" type="datetime-local" value={localVal} onChange={e => setForm({ ...form, hero_launch_at: e.target.value ? new Date(e.target.value).toISOString() : null })} data-testid="settings-launch-at" /></div>
    <div><label htmlFor="s-wa">WhatsApp</label><input id="s-wa" value={form.whatsapp || ""} onChange={e => setForm({ ...form, whatsapp: e.target.value })} data-testid="settings-whatsapp" /></div>
    <div><label htmlFor="s-ig">Instagram</label><input id="s-ig" value={form.instagram || ""} onChange={e => setForm({ ...form, instagram: e.target.value })} data-testid="settings-instagram" /></div>
    <button className="button neon" data-testid="save-settings-button">SAVE DROP <Check size={15} aria-hidden="true" /></button>
  </form>;
}

function PaymentScreenshot({ orderId }) {
  const [url, setUrl] = useState(""); const [err, setErr] = useState(""); const [full, setFull] = useState(false);
  useEffect(() => {
    let dead = false; let objUrl = "";
    axios.get(`${API}/admin/orders/${orderId}/payment-screenshot`, { responseType: "blob" })
      .then(r => { if (dead) return; objUrl = URL.createObjectURL(r.data); setUrl(objUrl); })
      .catch(e => setErr(extractError(e, "Screenshot could not be loaded")));
    return () => { dead = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [orderId]);
  if (err) return <div className="screenshot-err" data-testid={`screenshot-err-${orderId}`}>{err}</div>;
  if (!url) return <div className="screenshot-loading">Loading screenshot…</div>;
  return <>
    <img src={url} alt="Payment screenshot" className="screenshot-preview" data-testid={`screenshot-${orderId}`} onClick={() => setFull(true)} />
    <button type="button" className="link-btn" onClick={() => setFull(true)} data-testid={`open-screenshot-${orderId}`}>Enlarge ↗</button>
    {full && <div className="lightbox" onClick={() => setFull(false)} role="dialog" aria-modal="true" aria-label="Payment screenshot enlarged">
      <img src={url} alt="Payment screenshot enlarged" />
    </div>}
  </>;
}

function PaymentReview({ order, onDone }) {
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const decide = decision => {
    setBusy(true); setErr("");
    axios.post(`${API}/admin/orders/${order.id}/verify-payment`, { decision })
      .then(() => onDone()).catch(e => setErr(extractError(e, "Could not update"))).finally(() => setBusy(false));
  };
  return <div className="payment-review" data-testid={`payment-review-${order.id}`}>
    <div className="pr-meta">
      <p><b>Transaction reference:</b> <span data-testid={`utr-${order.id}`}>{order.payment_utr || "—"}</span></p>
      <p><b>Paid at:</b> {order.payment_paid_at || "—"}</p>
    </div>
    <PaymentScreenshot orderId={order.id} />
    <div className="review-actions">
      <button className="button neon small" disabled={busy} onClick={() => decide("verify")} data-testid={`verify-${order.id}`}>Verify payment</button>
      <button className="button ghost small" disabled={busy} onClick={() => decide("reject")} data-testid={`reject-${order.id}`}>Reject</button>
    </div>
    {err && <p className="form-error">{err}</p>}
  </div>;
}

function FulfillmentDropdown({ order, onSaved }) {
  const current = (order.fulfillment_status || "CONFIRMED").toUpperCase();
  const [val, setVal] = useState(current); const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  useEffect(() => setVal(current), [current]);
  const save = next => {
    if (next === current) return;
    setBusy(true); setErr("");
    axios.put(`${API}/admin/orders/${order.id}/fulfillment`, { status: next })
      .then(() => onSaved()).catch(e => { setErr(extractError(e, "Could not update")); setVal(current); })
      .finally(() => setBusy(false));
  };
  return <div className="fulfillment-control" data-testid={`fulfillment-control-${order.id}`}>
    <label htmlFor={`ff-${order.id}`} className="visually-hidden">Fulfilment status for {order.id}</label>
    <select id={`ff-${order.id}`} value={val} disabled={busy} onChange={e => { setVal(e.target.value); save(e.target.value); }} data-testid={`fulfillment-select-${order.id}`}>
      <option value="CONFIRMED">Confirmed</option>
      <option value="PACKED">Packed</option>
      <option value="SHIPPED">Shipped</option>
      <option value="DELIVERED">Delivered</option>
      <option value="CANCELLED">Cancelled</option>
    </select>
    {err && <em className="form-error inline">{err}</em>}
  </div>;
}

function ReturnControl({ order, onSaved }) {
  const [busy, setBusy] = useState(false);
  const rs = order.return_status;
  const decide = decision => { setBusy(true); axios.post(`${API}/admin/orders/${order.id}/return`, { decision }).then(() => onSaved()).finally(() => setBusy(false)); };
  if (!rs) return null;
  return <div className="return-control" data-testid={`return-control-${order.id}`}>
    <em>Return: {statusLabel(rs)}</em>
    {rs === "RETURN_REQUESTED" && <><button className="button neon small" disabled={busy} onClick={() => decide("approve")} data-testid={`return-approve-${order.id}`}>Approve</button><button className="button ghost small" disabled={busy} onClick={() => decide("reject")} data-testid={`return-reject-${order.id}`}>Reject</button></>}
    {rs === "RETURN_APPROVED" && <button className="button neon small" disabled={busy} onClick={() => decide("received")} data-testid={`return-received-${order.id}`}>Mark received</button>}
    {rs === "RETURN_RECEIVED" && <button className="button neon small" disabled={busy} onClick={() => decide("refund")} data-testid={`return-refund-${order.id}`}>Mark refund processed</button>}
  </div>;
}

function DeleteOrderButton({ order, enabled, onDeleted }) {
  const [open, setOpen] = useState(false); const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  if (!enabled) return null;
  const del = async () => {
    setBusy(true); setErr("");
    try {
      await axios.delete(`${API}/admin/orders/${order.id}`, { params: { confirm: "DELETE" } });
      setOpen(false); setConfirm(""); onDeleted();
    } catch (e) { setErr(extractError(e, "Could not delete this order.")); }
    finally { setBusy(false); }
  };
  return <>
    <button className="button ghost small danger" onClick={() => setOpen(true)} data-testid={`delete-order-${order.id}`}>Delete order</button>
    {open && <div className="modal-scrim" onClick={() => setOpen(false)}>
      <div className="modal-card confirm-card" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" data-testid={`delete-confirm-${order.id}`}>
        <p className="eyebrow">CONFIRM DELETION</p>
        <h3>Delete this order permanently?</h3>
        <p>This removes the order and its payment screenshot. Any active inventory reservation for this order will be released. Products, customers and settings are not affected.</p>
        <label className="span-2"><span>Type DELETE to confirm</span>
          <input value={confirm} onChange={e => setConfirm(e.target.value)} autoFocus data-testid={`delete-confirm-input-${order.id}`} />
        </label>
        {err && <p className="form-error" role="alert">{err}</p>}
        <div className="add-actions">
          <button className="button ghost" onClick={() => { setOpen(false); setConfirm(""); setErr(""); }}>CANCEL</button>
          <button className="button danger" disabled={busy || confirm !== "DELETE"} onClick={del} data-testid={`delete-confirm-button-${order.id}`}>{busy ? "DELETING..." : "DELETE ORDER"}</button>
        </div>
      </div>
    </div>}
  </>;
}

function CleanTestOrdersButton({ enabled, onDone }) {
  const [open, setOpen] = useState(false); const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(""); const [msg, setMsg] = useState("");
  if (!enabled) return null;
  const run = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await axios.post(`${API}/admin/orders/clean-test`, { confirm: "CLEAN TEST ORDERS" });
      setMsg(`Deleted ${r.data.orders_deleted} orders and ${r.data.screenshots_deleted} screenshots.`);
      setConfirm(""); onDone();
    } catch (e) { setErr(extractError(e, "Cleanup failed.")); }
    finally { setBusy(false); }
  };
  return <>
    <button className="button ghost small danger" onClick={() => setOpen(true)} data-testid="clean-test-orders-button">Clean test orders</button>
    {open && <div className="modal-scrim" onClick={() => setOpen(false)}>
      <div className="modal-card confirm-card" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" data-testid="clean-test-confirm">
        <p className="eyebrow">TESTING UTILITY</p>
        <h3>Clean all test orders?</h3>
        <p>This deletes every order and its payment screenshot. Products, customers, admin accounts and settings are not touched. Use only in a test environment.</p>
        <label className="span-2"><span>Type <b>CLEAN TEST ORDERS</b> to confirm</span>
          <input value={confirm} onChange={e => setConfirm(e.target.value)} autoFocus data-testid="clean-test-confirm-input" />
        </label>
        {err && <p className="form-error" role="alert">{err}</p>}
        {msg && <p className="form-success" role="status">{msg}</p>}
        <div className="add-actions">
          <button className="button ghost" onClick={() => { setOpen(false); setConfirm(""); setErr(""); setMsg(""); }}>CLOSE</button>
          <button className="button danger" disabled={busy || confirm !== "CLEAN TEST ORDERS"} onClick={run} data-testid="clean-test-run">{busy ? "CLEANING..." : "DELETE ALL ORDERS"}</button>
        </div>
      </div>
    </div>}
  </>;
}

function Admin() {
  const [authed, setAuthed] = useState(null);
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [products, setProducts] = useState([]); const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState({}); const [notifyReqs, setNotifyReqs] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [tab, setTab] = useState("dashboard"); const [message, setMessage] = useState("");
  const [showAdd, setShowAdd] = useState(false); const [editing, setEditing] = useState(null);
  const [payFilter, setPayFilter] = useState("all"); const [fulFilter, setFulFilter] = useState("all"); const [retFilter, setRetFilter] = useState("all");
  // Operational list = PAID orders only. Audit list = unsuccessful payment states
  // (PAYMENT_PENDING / PAYMENT_VERIFICATION_PENDING / PAYMENT_EXPIRED / REJECTED). The two
  // lists are fetched separately from the backend so a single truth exists on both ends.
  const [orderView, setOrderView] = useState("active");
  const [auditOrders, setAuditOrders] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const load = () => {
    axios.get(`${API}/admin/products`).then(r => setProducts(r.data)).catch(() => { });
    axios.get(`${API}/admin/orders`).then(r => setOrders(r.data)).catch(() => { });
    axios.get(`${API}/admin/orders`, { params: { view: "audit" } }).then(r => setAuditOrders(r.data)).catch(() => { });
    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => { });
    axios.get(`${API}/admin/notify-requests`).then(r => setNotifyReqs(r.data)).catch(() => { });
    axios.get(`${API}/admin/dashboard`).then(r => setDashboard(r.data)).catch(() => { });
  };
  useEffect(() => { axios.get(`${API}/admin/me`).then(() => { setAuthed(true); load(); }).catch(() => setAuthed(false)); }, []);
  const logout = () => { axios.post(`${API}/admin/logout`).finally(() => { setAuthed(false); setProducts([]); setOrders([]); setAuditOrders([]); setNotifyReqs([]); }); };
  const archiveProduct = async (p) => {
    if (!window.confirm(`Archive "${p.name}"? Customers will stop seeing it in the storefront.`)) return;
    try { await axios.delete(`${API}/admin/products/${p.id}`); load(); setMessage(`"${p.name}" archived.`); }
    catch (e) { setMessage(extractError(e, "Could not archive product.")); }
  };
  if (authed === null) return <main className="admin-login"><p className="eyebrow">CHECKING SESSION...</p></main>;
  if (authed === false) return <main className="admin-login">
    <p className="eyebrow">MIDNYTE CONTROL ROOM</p><h1>ADMIN LOGIN</h1>
    <form onSubmit={e => { e.preventDefault(); axios.post(`${API}/admin/login`, { email, password }).then(() => { setMessage(""); setAuthed(true); load(); }).catch(() => setMessage("Check your login details.")); }} data-testid="admin-login-form">
      <input data-testid="admin-email" value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="Email" autoComplete="username" required />
      <input data-testid="admin-password" value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="Password" autoComplete="current-password" required />
      <button className="button neon full" data-testid="admin-login-button">ENTER CONTROL ROOM <ArrowRight size={17} aria-hidden="true" /></button>
    </form>{message && <p className="form-error" data-testid="admin-login-error">{message}</p>}
  </main>;
  const sourceOrders = orderView === "audit" ? auditOrders : orders;
  const filteredOrders = sourceOrders.filter(o => {
    if (payFilter !== "all" && o.payment_status !== payFilter) return false;
    if (fulFilter !== "all" && (o.fulfillment_status || "NONE") !== fulFilter) return false;
    if (retFilter !== "all") {
      if (retFilter === "NONE") { if (o.return_status) return false; } else if (o.return_status !== retFilter) return false;
    }
    return true;
  });
  const q = productSearch.trim().toLowerCase();
  const filteredProducts = q
    ? products.filter(p => (p.name || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q))
    : products;
  const pendingReviewCount = auditOrders.filter(o => o.payment_status === "PAYMENT_VERIFICATION_PENDING").length;
  const auditCount = auditOrders.length;
  const returnsCount = orders.filter(o => o.return_status && o.return_status !== "REFUND_PROCESSED" && o.return_status !== "RETURN_REJECTED").length;
  const testCleanupEnabled = !!(dashboard && dashboard.capabilities && dashboard.capabilities.test_cleanup_enabled);
  const hardDeleteEnabled = !!(dashboard && dashboard.capabilities && dashboard.capabilities.order_hard_delete_enabled);
  const tabs = [
    ["dashboard", "DASHBOARD", LayoutDashboard, 0],
    ["products", "PRODUCTS", Package, 0],
    ["orders", "ORDERS", Package, pendingReviewCount],
    ["returns", "RETURNS", ArrowRight, returnsCount],
    ["settings", "SETTINGS", Bell, 0],
  ];
  return <main className="admin-page">
    <div className="admin-sidebar">
      <p className="eyebrow">CONTROL ROOM</p>
      {tabs.map(([k, l, Ic, badge]) => <button key={k} className={tab === k ? "active" : ""} onClick={() => setTab(k)} data-testid={`admin-${k}-tab`}><Ic size={17} aria-hidden="true" /> {l}{badge > 0 && <b>{badge}</b>}</button>)}
      <button onClick={logout} data-testid="admin-logout-button"><LogOut size={17} aria-hidden="true" /> LOG OUT</button>
    </div>
    <div className="admin-content">
      <div className="admin-heading"><div><p className="eyebrow">OVERVIEW / {tab.toUpperCase()}</p><h1>{tab.toUpperCase()}</h1></div>{tab === "products" && <button className="button neon" onClick={() => setShowAdd(true)} data-testid="open-add-product-button"><Plus size={17} aria-hidden="true" /> ADD PRODUCT</button>}</div>
      {message && <div className="admin-notice" data-testid="admin-notice">{message}<button onClick={() => setMessage("")} aria-label="Dismiss"><X size={15} aria-hidden="true" /></button></div>}

      {tab === "dashboard" && dashboard && <div className="dashboard-grid" data-testid="admin-dashboard">
        <div className="dash-card"><h3>Orders</h3>
          <p>Total <b data-testid="d-orders-total">{dashboard.orders.total}</b></p>
          <p>Verification pending <b data-testid="d-verify-pending">{dashboard.orders.verification_pending}</b></p>
          <p>Paid <b>{dashboard.orders.paid}</b> · Rejected <b>{dashboard.orders.rejected}</b></p>
          <p>Confirmed <b>{dashboard.orders.CONFIRMED}</b> · Packed <b>{dashboard.orders.PACKED}</b></p>
          <p>Shipped <b>{dashboard.orders.SHIPPED}</b> · Delivered <b>{dashboard.orders.DELIVERED}</b></p>
          <p>Cancelled <b>{dashboard.orders.CANCELLED}</b></p>
          <div className="dash-actions"><button className="button ghost small" onClick={() => { setTab("orders"); setPayFilter("PAYMENT_VERIFICATION_PENDING"); }} data-testid="dash-verify-shortcut">Payment verification →</button></div>
        </div>
        <div className="dash-card"><h3>Returns</h3>
          <p>Requested <b>{dashboard.returns.RETURN_REQUESTED}</b></p>
          <p>Approved <b>{dashboard.returns.RETURN_APPROVED}</b></p>
          <p>Received <b>{dashboard.returns.RETURN_RECEIVED}</b></p>
          <p>Refund processed <b>{dashboard.returns.REFUND_PROCESSED}</b></p>
          <p>Rejected <b>{dashboard.returns.RETURN_REJECTED}</b></p>
          <div className="dash-actions"><button className="button ghost small" onClick={() => setTab("returns")}>Manage returns →</button></div>
        </div>
        <div className="dash-card"><h3>Inventory</h3>
          <p>Products <b>{dashboard.products.total}</b></p>
          <p>Low stock <b>{dashboard.products.low_stock}</b></p>
          <p>Out of stock <b>{dashboard.products.out_of_stock}</b></p>
          <div className="dash-actions"><button className="button ghost small" onClick={() => setTab("products")}>Manage products →</button><button className="button neon small" onClick={() => { setTab("products"); setTimeout(() => setShowAdd(true), 60); }}>Add product</button></div>
        </div>
      </div>}

      {tab === "orders" && <>
        <div className="admin-view-tabs" data-testid="admin-order-view-tabs" role="tablist" aria-label="Orders view">
          <button role="tab" aria-selected={orderView === "active"} className={orderView === "active" ? "active" : ""}
            onClick={() => { setOrderView("active"); setPayFilter("all"); setFulFilter("all"); setRetFilter("all"); }}
            data-testid="admin-orders-tab-active">
            Active orders <b data-testid="admin-active-count">{orders.length}</b>
          </button>
          <button role="tab" aria-selected={orderView === "audit"} className={orderView === "audit" ? "active" : ""}
            onClick={() => { setOrderView("audit"); setPayFilter("all"); setFulFilter("all"); setRetFilter("all"); }}
            data-testid="admin-orders-tab-audit">
            Needs attention{auditCount > 0 && <b data-testid="admin-audit-count">{auditCount}</b>}
          </button>
        </div>
        {orderView === "audit" && <p className="audit-view-blurb" data-testid="admin-audit-blurb">
          These orders did not complete payment. Fulfilment is unavailable until payment is successfully verified.
          Verification-pending orders can be approved or rejected from this list; expired or rejected orders stay here for audit.
        </p>}
        <div className="admin-filters" data-testid="admin-order-filters">
          <label>Payment
            <select value={payFilter} onChange={e => setPayFilter(e.target.value)} data-testid="filter-payment">
              {orderView === "active"
                ? <><option value="all">All (paid)</option><option value="PAID">Paid</option></>
                : <><option value="all">All</option>
                    <option value="PAYMENT_PENDING">Pending</option>
                    <option value="PAYMENT_VERIFICATION_PENDING">Verification pending</option>
                    <option value="PAYMENT_EXPIRED">Expired</option>
                    <option value="REJECTED">Rejected</option></>}
            </select></label>
          <label>Fulfilment
            <select value={fulFilter} onChange={e => setFulFilter(e.target.value)} data-testid="filter-fulfillment" disabled={orderView === "audit"}>
              <option value="all">All</option><option value="NONE">Awaiting payment</option>
              <option value="CONFIRMED">Confirmed</option><option value="PACKED">Packed</option>
              <option value="SHIPPED">Shipped</option><option value="DELIVERED">Delivered</option>
              <option value="CANCELLED">Cancelled</option>
            </select></label>
          <label>Return
            <select value={retFilter} onChange={e => setRetFilter(e.target.value)} data-testid="filter-return" disabled={orderView === "audit"}>
              <option value="all">All</option><option value="NONE">No return</option>
              <option value="RETURN_REQUESTED">Requested</option><option value="RETURN_APPROVED">Approved</option>
              <option value="RETURN_RECEIVED">Received</option><option value="REFUND_PROCESSED">Refunded</option>
              <option value="RETURN_REJECTED">Rejected</option>
            </select></label>
        </div>
        <div className="admin-table" data-testid="admin-orders-table">
          {filteredOrders.length === 0 && <div className="empty-state" data-testid="admin-orders-empty">NO ORDERS MATCH.</div>}
          {filteredOrders.map(o => <div className="admin-order-card" key={o.id} data-testid={`admin-order-${o.id}`}>
            <div className="ao-head">
              <div>
                <strong>{o.id}</strong>
                <span>{new Date(o.created_at).toLocaleString()}</span>
                <span>{o.customer?.customer_name} · {o.customer?.phone}</span>
                <span>{o.customer?.address}, {o.customer?.city} {o.customer?.pincode}</span>
              </div>
              <div className="ao-badges">
                <em className="badge-pay" data-testid={`pay-${o.id}`}>Payment: {statusLabel(o.payment_status)}</em>
                <em className="badge-ful" data-testid={`ful-${o.id}`}>Fulfilment: {o.fulfillment_status ? statusLabel(o.fulfillment_status) : "—"}</em>
                {o.return_status && <em className="badge-ret" data-testid={`ret-${o.id}`}>Return: {statusLabel(o.return_status)}</em>}
                <b>{money(o.total)}</b>
              </div>
            </div>
            <div className="ao-items">
              {o.items.map(it => <span key={it.product_id} className="ao-item"><b>{it.name}</b> × {it.quantity} · {money(it.price)}</span>)}
            </div>
            {o.payment_status === "PAYMENT_VERIFICATION_PENDING" && <PaymentReview order={o} onDone={load} />}
            {o.payment_status === "PAID" && <FulfillmentDropdown order={o} onSaved={load} />}
            {o.payment_status !== "PAID" && o.payment_status !== "PAYMENT_VERIFICATION_PENDING" &&
              <p className="audit-no-fulfillment" data-testid={`no-fulfillment-${o.id}`}>
                Fulfilment is unavailable because payment was not successfully completed ({statusLabel(o.payment_status)}).
              </p>}
            <ReturnControl order={o} onSaved={load} />
            <div className="ao-footer"><DeleteOrderButton order={o} enabled={hardDeleteEnabled} onDeleted={load} /></div>
          </div>)}
        </div></>}

      {tab === "returns" && <div className="admin-table" data-testid="admin-returns-table">
        {orders.filter(o => o.return_status).length === 0
          ? <div className="empty-state">NO RETURN REQUESTS.</div>
          : orders.filter(o => o.return_status).map(o => <div className="admin-order-card" key={o.id} data-testid={`return-order-${o.id}`}>
            <div className="ao-head">
              <div><strong>{o.id}</strong><span>{o.customer?.customer_name}</span><span>{o.return_reason || ""}</span></div>
              <div className="ao-badges"><em className="badge-ret">{statusLabel(o.return_status)}</em><b>{money(o.total)}</b></div>
            </div>
            <div className="ao-items">
              {o.items.map(it => <span key={it.product_id} className="ao-item"><b>{it.name}</b> × {it.quantity}</span>)}
            </div>
            <ReturnControl order={o} onSaved={load} />
          </div>)}
      </div>}

      {tab === "products" && <>
        <div className="admin-filters">
          <label className="span-2" style={{ flex: "1 1 260px" }}>Search
            <input type="search" value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Search by name or SKU" data-testid="admin-product-search" />
          </label>
        </div>
        <BulkUpload onDone={load} />
        <div className="admin-table" data-testid="admin-products-table">
          <div className="table-head"><span>PRODUCT</span><span>SKU</span><span>PRICE / MRP</span><span>STOCK</span><span>STATUS</span><span>ACTIONS</span></div>
          {filteredProducts.length === 0 && <div className="empty-state">NO PRODUCTS MATCH.</div>}
          {filteredProducts.map(p => <div className="table-row" key={p.id}>
            <div className="admin-product"><img src={p.images?.[0] || LOGO} alt="" /><span>{p.name}<small>{labelFor(p.collection) || p.category}</small></span></div>
            <span>{p.sku}</span>
            <span>{money(p.price)}<small> / {money(p.mrp)}</small></span>
            <StockCell product={p} onSaved={load} />
            <span>
              <em className={`status-tag ${p.stock <= 0 ? "out" : p.stock <= (p.low_stock_threshold || 3) ? "low" : "in"}`}>{p.stock <= 0 ? "Out of stock" : p.stock <= (p.low_stock_threshold || 3) ? `Low · ${p.stock}` : `In stock · ${p.stock}`}</em>
              {!p.published && <em className="status-tag out" style={{ marginTop: 4 }}>Unpublished</em>}
            </span>
            <span className="admin-actions">
              <button className="button ghost small" onClick={() => setEditing(p)} data-testid={`edit-${p.id}`}>Edit</button>
              <button aria-label={`Archive ${p.name}`} data-testid={`archive-${p.id}`} onClick={() => archiveProduct(p)}><Archive size={17} aria-hidden="true" /></button>
            </span>
          </div>)}
        </div></>}

      {tab === "settings" && <div className="admin-settings">
        <DropSettings settings={settings} onSaved={s => { setSettings(s); setMessage("Settings saved."); }} />
        <section className="settings-block">
          <h3>Waitlist</h3>
          {notifyReqs.length === 0 ? <p className="empty-state">No waitlist entries yet.</p>
            : <div className="admin-table" data-testid="admin-notify-table">
              {notifyReqs.map((r, i) => <div className="notify-row" key={i}><b>{r.product_name}</b><span>{r.email}</span><small>{new Date(r.created_at).toLocaleString()}</small></div>)}
            </div>}
        </section>
        <section className="settings-block">
          <h3>Testing utilities</h3>
          {testCleanupEnabled
            ? <p>This environment allows deleting every order for testing. Use with care.</p>
            : <p>Test cleanup is disabled in this environment.</p>}
          <CleanTestOrdersButton enabled={testCleanupEnabled} onDone={() => { load(); setMessage("Test orders cleaned."); }} />
        </section>
      </div>}
    </div>
    {showAdd && <AddProduct onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); setMessage("New piece added to your catalogue."); }} />}
    {editing && <EditProduct product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); setMessage("Product updated."); }} />}
  </main>;
}

// ── Cookie banner + footer ────────────────────────────────────────
function CookieBanner() {
  const [choice, setChoice] = useState(() => { try { return localStorage.getItem("midnyte_consent") || ""; } catch { return ""; } });
  useEffect(() => {
    if (typeof window === "undefined" || !window.posthog || typeof window.posthog.opt_out_capturing !== "function") return;
    if (choice === "accepted") {
      window.posthog.opt_in_capturing();
      try { window.posthog.startSessionRecording && window.posthog.startSessionRecording(); } catch (e) {}
    } else {
      window.posthog.opt_out_capturing();
      try { window.posthog.stopSessionRecording && window.posthog.stopSessionRecording(); } catch (e) {}
    }
  }, [choice]);
  useEffect(() => {
    const visible = !(choice === "accepted" || choice === "declined");
    document.body.classList.toggle("has-cookie-banner", visible);
    return () => document.body.classList.remove("has-cookie-banner");
  }, [choice]);
  const set = v => { try { localStorage.setItem("midnyte_consent", v); } catch { } setChoice(v); };
  if (choice === "accepted" || choice === "declined") return null;
  return <div className="cookie-banner" role="dialog" aria-live="polite" aria-label="Cookie and analytics consent" data-testid="cookie-banner">
    <p>We use one strictly-necessary cart storage entry and, only with your consent, PostHog product analytics (including session replay) to debug the storefront. Read our <Link to="/privacy">Privacy Policy</Link>.</p>
    <div className="cookie-actions">
      <button className="button ghost small" onClick={() => set("declined")} data-testid="cookie-decline">DECLINE</button>
      <button className="button neon small" onClick={() => set("accepted")} data-testid="cookie-accept">ACCEPT</button>
    </div>
  </div>;
}

function CookiePrefsLink() {
  const reset = () => { try { localStorage.removeItem("midnyte_consent"); } catch { } window.location.reload(); };
  return <button className="footer-link" onClick={reset} data-testid="footer-cookie-prefs">COOKIE PREFERENCES</button>;
}

function Shell() {
  const { me, refresh, signOut } = useAuth();
  const [cart, setCart] = useState(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("midnyte_cart") || "[]");
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(x => x && x.p && typeof x.p === "object" && typeof x.quantity === "number");
    } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const save = c => { setCart(c); localStorage.setItem("midnyte_cart", JSON.stringify(c)); };
  const add = p => {
    const next = cart.some(x => x.p.id === p.id) ? cart.map(x => x.p.id === p.id ? { ...x, quantity: x.quantity + 1 } : x) : [...cart, { p, quantity: 1 }];
    save(next); setOpen(true);
  };
  const update = (id, n) => save(cart.map(x => x.p.id === id ? { ...x, quantity: x.quantity + n } : x).filter(x => x.quantity > 0));
  return <>
    <a className="skip-link" href="#main-content">Skip to content</a>
    <Header cartCount={cart.reduce((a, x) => a + x.quantity, 0)} onCart={() => setOpen(true)} me={me} onSignOut={() => { signOut(); navigate("/"); }} />
    <div id="main-content" tabIndex={-1}>
      <Routes>
        <Route path="/" element={<Home onAdd={add} />} />
        <Route path="/shop" element={<Shop onAdd={add} />} />
        <Route path="/product/:slug" element={<ProductPage onAdd={add} />} />
        <Route path="/checkout" element={<Checkout cart={cart} onDone={() => save([])} onCartSync={save} me={me} />} />
        <Route path="/login" element={me === undefined ? null : me ? <Navigate to="/account" replace /> : <Login onAuthed={refresh} />} />
        <Route path="/register" element={me === undefined ? null : me ? <Navigate to="/account" replace /> : <Register onAuthed={refresh} />} />
        <Route path="/account" element={me === undefined ? null : me ? <Account me={me} refresh={refresh} /> : <Navigate to="/login?next=/account" replace />} />
        <Route path="/orders" element={me === undefined ? null : me ? <Orders /> : <Navigate to="/login?next=/orders" replace />} />
        <Route path="/orders/:id" element={me === undefined ? null : me ? <OrderDetail /> : <Navigate to="/login" replace />} />
        <Route path="/control-room-9f2a" element={<Admin />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/shipping" element={<Shipping />} />
        <Route path="/returns" element={<Returns />} />
        <Route path="/contact" element={<Contact />} />
      </Routes>
    </div>
    {open && <>
      <div className="scrim" onClick={() => setOpen(false)} data-testid="cart-scrim" />
      <Cart cart={cart} onClose={() => setOpen(false)} onUpdate={update} onCheckout={() => { setOpen(false); navigate(me ? "/checkout" : "/login?next=/checkout"); }} />
    </>}
    <footer>
      <div className="footer-top">
        <Link to="/" className="logo" aria-label="MIDNYTE home"><BrandMark size={44} /></Link>
        <nav className="footer-nav" aria-label="Legal">
          <Link to="/privacy" data-testid="footer-privacy">PRIVACY</Link>
          <Link to="/terms" data-testid="footer-terms">TERMS</Link>
          <Link to="/shipping" data-testid="footer-shipping">SHIPPING</Link>
          <Link to="/returns" data-testid="footer-returns">RETURNS &amp; REFUNDS</Link>
          <Link to="/contact" data-testid="footer-contact">CONTACT / GRIEVANCE</Link>
          <CookiePrefsLink />
        </nav>
      </div>
      <div className="footer-bottom">
        <span>© MIDNYTE by Cynalp Enterprises · 2026</span>
        <span>Prices in INR, inclusive of applicable taxes.</span>
      </div>
    </footer>
    <CookieBanner />
  </>;
}

export default function Root() { return <BrowserRouter><Shell /></BrowserRouter>; }
